import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import authService from '../services/authService';

function AdminStats() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [setupInstructions, setSetupInstructions] = useState('');
  const [dashboardUrl, setDashboardUrl] = useState('');

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    if (!authResult.user?.is_admin) {
      navigate('/dashboard');
      return;
    }

    await loadStats();
  };

  const loadStats = async () => {
    setLoading(true);
    setError('');
    setSetupInstructions('');

    try {
      const response = await fetch('/api/admin/posthog-stats', {
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
        setDashboardUrl(data.posthog_dashboard_url);
      } else {
        if (data.setup_instructions) {
          setSetupInstructions(data.setup_instructions);
        }
        setError(data.error || 'Failed to load analytics');
      }
    } catch (err) {
      setError('Failed to connect to analytics service');
    }

    setLoading(false);
  };

  const StatCard = ({ title, value, subtitle, variant = 'primary' }) => (
    <Card className="h-100 text-center">
      <Card.Body>
        <h2 className={`text-${variant} mb-1`}>{value}</h2>
        <div className="text-muted small">{title}</div>
        {subtitle && <div className="text-muted small mt-1">{subtitle}</div>}
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading analytics...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Product Analytics</h1>
          <p className="text-muted">PostHog event tracking for AIPE</p>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={loadStats}>
            Refresh
          </Button>
          <Link to="/admin" className="btn btn-outline-primary">
            Back to Admin
          </Link>
          {dashboardUrl && (
            <a
              href="https://us.posthog.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Open PostHog Dashboard
            </a>
          )}
        </Col>
      </Row>

      {error && (
        <Alert variant="warning">
          <Alert.Heading>Analytics Not Available</Alert.Heading>
          <p>{error}</p>
          {setupInstructions && (
            <>
              <hr />
              <p className="mb-0">{setupInstructions}</p>
            </>
          )}
        </Alert>
      )}

      {!stats && !error && (
        <Alert variant="info">
          <Alert.Heading>Setup Required</Alert.Heading>
          <p>To view analytics here, you need to:</p>
          <ol>
            <li>Go to <a href="https://us.posthog.com" target="_blank" rel="noopener noreferrer">PostHog</a></li>
            <li>Navigate to Settings → Personal API Keys</li>
            <li>Create a new API key</li>
            <li>Add it as <code>POSTHOG_PERSONAL_API_KEY</code> environment variable</li>
          </ol>
          <p className="mb-0">
            In the meantime, you can view all analytics directly in{' '}
            <a href="https://us.posthog.com" target="_blank" rel="noopener noreferrer">PostHog Dashboard</a>
          </p>
        </Alert>
      )}

      {stats && (
        <>
          {/* Period */}
          <Alert variant="light" className="mb-4">
            <strong>Period:</strong> {stats.period} (Last 30 days)
          </Alert>

          {/* Summary Cards */}
          <h4 className="mb-3">Summary</h4>
          <Row className="mb-4 g-3">
            <Col md={3}>
              <StatCard
                title="Lead Magnet Starts"
                value={stats.totals?.lead_magnet_starts || 0}
                subtitle="Mind to Method sessions"
                variant="primary"
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Lead Magnet Completions"
                value={stats.totals?.lead_magnet_completions || 0}
                subtitle={`${stats.totals?.lead_magnet_conversion || 0}% conversion`}
                variant="success"
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Target Market Starts"
                value={stats.totals?.target_market_starts || 0}
                subtitle="Discovery sessions"
                variant="info"
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Target Market Completions"
                value={stats.totals?.target_market_completions || 0}
                subtitle={`${stats.totals?.target_market_conversion || 0}% conversion`}
                variant="success"
              />
            </Col>
          </Row>

          <Row className="mb-4 g-3">
            <Col md={3}>
              <StatCard
                title="Logins"
                value={stats.totals?.logins || 0}
                variant="secondary"
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Signups"
                value={stats.totals?.signups || 0}
                variant="warning"
              />
            </Col>
          </Row>

          {/* Detailed Events */}
          <h4 className="mb-3">All Events</h4>
          <Card>
            <Card.Body>
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Event Name</th>
                    <th>Count</th>
                    <th>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(stats.events || {}).map(([event, count]) => {
                    let category = 'Other';
                    let categoryColor = 'secondary';

                    if (event.startsWith('m2m_')) {
                      category = 'Mind to Method';
                      categoryColor = 'primary';
                    } else if (event.startsWith('tm_')) {
                      category = 'Target Market';
                      categoryColor = 'info';
                    } else if (event.startsWith('cc_')) {
                      category = 'Command Center';
                      categoryColor = 'success';
                    } else if (event.startsWith('auth_')) {
                      category = 'Authentication';
                      categoryColor = 'warning';
                    }

                    return (
                      <tr key={event}>
                        <td><code>{event}</code></td>
                        <td>
                          {typeof count === 'number' ? (
                            <strong>{count}</strong>
                          ) : (
                            <span className="text-danger">{count}</span>
                          )}
                        </td>
                        <td>
                          <Badge bg={categoryColor}>{category}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Quick Links */}
          <Card className="mt-4">
            <Card.Header>Quick Links</Card.Header>
            <Card.Body>
              <p className="mb-2">
                <a href="https://us.posthog.com" target="_blank" rel="noopener noreferrer">
                  PostHog Dashboard
                </a>
                {' '}- Full analytics dashboard with charts, funnels, and session recordings
              </p>
              <p className="mb-0">
                <a href="https://us.posthog.com/events" target="_blank" rel="noopener noreferrer">
                  Live Events
                </a>
                {' '}- See events as they happen in real-time
              </p>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
}

export default AdminStats;
